import React, { useState, useEffect } from 'react';
import { Text } from '@heartinternet/hi-ui';
import { Select } from '@heartinternet/hi-ui';
import { Notice } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Link } from '@heartinternet/hi-ui';
import { Spinner } from '@heartinternet/hi-ui';
import { Card } from '@heartinternet/hi-ui';
import { Form } from '@heartinternet/hi-ui';
import { FormField } from '@heartinternet/hi-ui';
import type { AddHostingPageProps, ResoldType, AvailableDomain, HostingType } from './types';
import './AddHostingPage.css';

export type { AddHostingPageProps };

const RESOLD_TYPES_ENDPOINT = '/manage/data.cgi/resold_types';
const DOMAINS_ENDPOINT = '/manage/data.cgi/domains_available_to_attach';
const FORM_ACTION = '/manage/manage-packages-w.cgi/add';

export const AddHostingPage: React.FC<AddHostingPageProps> = ({ hasHemsx, hemsxServers }) => {
  const [resoldTypes, setResoldTypes] = useState<ResoldType[]>([]);
  const [domains, setDomains] = useState<AvailableDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedDomain, setSelectedDomain] = useState('');
  const [selectedType, setSelectedType] = useState<HostingType>('linux');
  const [selectedServer, setSelectedServer] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [typesResponse, domainsResponse] = await Promise.all([
          fetch(RESOLD_TYPES_ENDPOINT),
          fetch(DOMAINS_ENDPOINT),
        ]);
        const typesData: ResoldType[] = await typesResponse.json();
        const domainsData: AvailableDomain[] = await domainsResponse.json();
        setResoldTypes(typesData);
        setDomains(domainsData);
        if (domainsData.length > 0) {
          setSelectedDomain(domainsData[0].name);
        }
      } catch {
        setError('Failed to load data. Please refresh the page and try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter packages by selected type — premium uses linux packages
  const webServerType = selectedType === 'use-my-webserver' ? 'linux' : selectedType;
  const filteredPackages = resoldTypes.filter((t) => t.web_server_type === webServerType);

  // Set default package when filtered list changes
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect -- intentional: default-select first package (or clear) when the filtered list resolves */
    if (filteredPackages.length > 0) {
      setSelectedPackage(filteredPackages[0].id);
    } else {
      setSelectedPackage('');
    }
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [selectedType, resoldTypes.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set default server when premium is selected
  const serverEntries = Object.entries(hemsxServers);
  useEffect(() => {
    if (selectedType === 'use-my-webserver' && serverEntries.length > 0 && !selectedServer) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: default to the first available server when premium type is picked
      setSelectedServer(serverEntries[0][0]);
    }
  }, [selectedType]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasDomains = domains.length > 0;
  const hasPackages = filteredPackages.length > 0;
  const canSubmit = hasDomains && hasPackages;

  return (
    <div className="hi-ui-add-hosting">
      <Text variant="heading-1">Add Hosting Accounts</Text>

      <Text variant="body" className="hi-ui-add-hosting__intro">
        The following list shows domain names on your account that are not yet assigned to a web
        hosting package. To assign a domain name to a web hosting package select the domain and then
        the web hosting package you would like to assign it to and click &quot;Activate
        Hosting&quot;. This will create the web hosting package and issue a welcome email.
      </Text>

      <Text variant="body" className="hi-ui-add-hosting__intro">
        If you need to add a new domain name you can either{' '}
        <Link href="/manage/domain.cgi">order a new one</Link>,{' '}
        <Link href="/manage/transfer.cgi">transfer an existing domain</Link> or{' '}
        <Link href="/manage/soft_transfer.cgi">use a domain reference</Link>.
      </Text>

      {error && (
        <Notice variant="error" className="hi-ui-add-hosting__error">
          {error}
        </Notice>
      )}

      {loading ? (
        <div className="hi-ui-add-hosting__loading">
          <Spinner size="medium" />
        </div>
      ) : !error ? (
        <Card variant="surface" className="hi-ui-add-hosting__card">
          {!hasDomains ? (
            <Text variant="body" inline>
              You have no domain names currently available to assign to a hosting package. Please{' '}
              <Link href="/manage/domain.cgi">register</Link>,{' '}
              <Link href="/manage/transfer.cgi">transfer</Link>, or{' '}
              <Link href="/manage/soft_transfer.cgi">add a reference</Link> for one.
            </Text>
          ) : (
            <Form action={FORM_ACTION} method="POST">
              <div className="hi-ui-add-hosting__fields">
                <FormField label="Domain" labelPosition="left" fullWidth>
                  <Select
                    name="add_domain"
                    value={selectedDomain}
                    onChange={(e) => setSelectedDomain(e.target.value)}
                    fullWidth
                  >
                    {domains.map((d) => (
                      <option key={d.id} value={d.name}>
                        {d.name}
                        {d.flags.extraSite ? ' (in use as extra site)' : ''}
                      </option>
                    ))}
                  </Select>
                </FormField>

                <FormField label="Type" labelPosition="left" fullWidth>
                  <Select
                    name="type"
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value as HostingType)}
                    fullWidth
                  >
                    {hasHemsx && <option value="use-my-webserver">Premium Hosting (Linux)</option>}
                    <option value="linux">Linux</option>
                    <option value="windows">Windows</option>
                  </Select>
                </FormField>

                {selectedType === 'use-my-webserver' && (
                  <FormField label="Server" labelPosition="left" fullWidth>
                    <Select
                      name="my-webserver"
                      value={selectedServer}
                      onChange={(e) => setSelectedServer(e.target.value)}
                      fullWidth
                    >
                      {serverEntries.map(([oid, name]) => (
                        <option key={oid} value={oid}>
                          {name}
                        </option>
                      ))}
                    </Select>
                  </FormField>
                )}

                <FormField label="Package" labelPosition="left" fullWidth>
                  {hasPackages ? (
                    <Select
                      name="add_type"
                      value={selectedPackage}
                      onChange={(e) => setSelectedPackage(e.target.value)}
                      fullWidth
                    >
                      {filteredPackages.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Text variant="body" inline>
                      No packages available for this hosting type.{' '}
                      <Link href="/manage/manage-package-types.cgi">Click here to add one.</Link>
                    </Text>
                  )}
                </FormField>
              </div>

              <div className="hi-ui-add-hosting__submit">
                <Button type="submit" variant="primary" disabled={!canSubmit}>
                  Activate Hosting
                </Button>
              </div>
            </Form>
          )}
        </Card>
      ) : null}
    </div>
  );
};
